/**
 * Captures the buzz winner's spoken answer on this client (#91, D2/§5.3).
 *
 * When the authoritative state says it is THIS player's turn to answer (they hold the
 * buzzer in an AUTO game — {@link isMySpokenAnswerTurn}) and STT is supported, it
 * starts the mic, streams a live transcript, and — on the natural end of speech —
 * submits `submit_spoken_answer`. The player can also submit early (a button) or
 * re-record. Where STT is unsupported or the mic is denied, the same controller backs
 * a **typed** fallback that submits the identical command (§5.3), so the mode is
 * playable on any browser.
 *
 * Only the transcript is ever sent — raw audio never leaves the browser (§9).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Command, GameState } from '@jeopardy/shared';
import { isMySpokenAnswerTurn } from '../game/voice-selectors';
import { getSpeechRecognizer, type SpeechRecognizer } from './speech-recognition';

export interface AnswerCaptureController {
  /** True when the spoken-answer UI should render for this player. */
  active: boolean;
  /** True when browser STT is available (else the typed fallback is shown). */
  supported: boolean;
  /** True while the mic is capturing. */
  listening: boolean;
  /** The live (or typed) transcript. */
  transcript: string;
  /** Edit the transcript directly (the typed fallback / manual correction). */
  setTranscript: (text: string) => void;
  /** A recognition error (e.g. the mic was denied), or null. */
  error: string | null;
  /** Submit the current transcript as the spoken answer (one action, FR-9.5). */
  submit: () => void;
  /** Restart mic capture (re-record). */
  restart: () => void;
}

export interface UseAnswerCaptureParams {
  state: GameState | null;
  playerId: string | null;
  send: (command: Command) => void;
  /** Injectable for tests; defaults to the shared browser recognizer. */
  recognizer?: SpeechRecognizer;
}

export function useAnswerCapture({
  state,
  playerId,
  send,
  recognizer,
}: UseAnswerCaptureParams): AnswerCaptureController {
  const active = isMySpokenAnswerTurn(state, playerId);
  const clueId = state?.jeopardy.clueState?.clueId ?? null;

  // Re-resolve the recognizer whenever an answer turn BEGINS (`active`), never only once
  // at mount. Backend STT (Transcribe, #93) is enabled by `/api/config`, which resolves
  // asynchronously AFTER the page mounts; `configureVoiceBackend` then re-selects the
  // factory singleton. A mount-time `useMemo` (keyed only on the injectable prop) would
  // pin the pre-config browser recognizer for the component's whole life. An answer turn
  // only opens once a game is live (well after config), so re-selecting on that edge
  // yields the backend recognizer when the deployment advertises STT. `getSpeechRecognizer()`
  // returns a stable singleton, so recomputing returns the same instance (no churn).
  const rec = useMemo(() => recognizer ?? getSpeechRecognizer(), [recognizer, active]);
  const supported = rec.isSupported();

  const [transcript, setTranscript] = useState('');
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guard against a double submit (e.g. a manual submit racing the natural onEnd).
  const submittedRef = useRef(false);

  const submit = useCallback(() => {
    if (clueId === null || submittedRef.current) return;
    const text = transcript.trim();
    if (text === '') return;
    submittedRef.current = true;
    rec.stop();
    setListening(false);
    send({ type: 'submit_spoken_answer', clueId, transcript: text });
  }, [clueId, transcript, rec, send]);

  const beginListening = useCallback(() => {
    if (!supported) return;
    submittedRef.current = false;
    setTranscript('');
    setError(null);
    setListening(true);
    rec.start({
      onResult: (r) => setTranscript(r.transcript),
      onEnd: (finalText) => {
        // Prefer the settled final transcript: the backend (Transcribe) revises the
        // text on finalization, and the browser recognizer's onEnd also carries the
        // settled phrase — either way it supersedes the last interim partial. Guard on
        // non-empty so an empty flush never clears a good transcript.
        if (finalText.trim()) setTranscript(finalText);
        setListening(false);
      },
      onError: (e) => {
        setError(e);
        setListening(false);
      },
    });
  }, [supported, rec]);

  const restart = useCallback(() => {
    if (!supported) return;
    rec.abort();
    beginListening();
  }, [supported, rec, beginListening]);

  // Auto-start the mic when it becomes this player's turn; tear it down when the turn
  // ends (answer submitted → JUDGING, clue changed, or unmount).
  useEffect(() => {
    if (!active) return;
    if (supported) {
      beginListening();
      return () => {
        rec.abort();
        setListening(false);
      };
    }
    // Unsupported: reset the typed-fallback field for a fresh turn.
    submittedRef.current = false;
    setTranscript('');
    return undefined;
    // Re-arm per clue turn (clueId), not on every state version.
  }, [active, supported, clueId]);

  return {
    active,
    supported,
    listening,
    transcript,
    setTranscript,
    error,
    submit,
    restart,
  };
}
